import { zodResolver } from "@hookform/resolvers/zod";
import * as Dialog from "@radix-ui/react-dialog";
import { AxiosError } from "axios";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Button } from "~/components/Button";
import { ResendTokenTimer } from "~/components/ResendTokenTimer";
import { InputControl } from "~/components/form/InputControl";
import { AuthAPI, Language } from "~/lib/api/auth";
import { TEMPLATE } from "~/utils/constants";
import {
  ForgotPasswordFormInput,
  forgotPasswordValidationSchema,
} from "~/validation/forgot-password";
import { ModalCloseButton } from "./ModalCloseButton";

interface SendResetPasswordDialogFormProps {
  onClose: () => void;
  isRetry?: boolean;
  onResendToken?: (email: string) => Promise<void>;
}

export const SendResetPasswordDialogForm: React.FC<
  SendResetPasswordDialogFormProps
> = ({ onClose, isRetry, onResendToken }) => {
  const { query, locale } = useRouter();
  const [isBlocked, setIsBlocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSentAt, setLastSentAt] = useState(() => new Date());
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormInput>({
    resolver: zodResolver(forgotPasswordValidationSchema),
    defaultValues: {
      email: query.email ? String(query.email) : "",
    },
  });
  const t = useTranslations();

  useEffect(() => {
    if (!isBlocked) return;

    const timeout = setTimeout(
      () => {
        setIsBlocked(false);
      },
      1000 * 60 * 10,
    ); // 10 minutos

    return () => {
      clearTimeout(timeout);
    };
  }, [isBlocked]);

  const handleSubmit = handleFormSubmit(async values => {
    setIsSubmitting(true);

    const currentLocale = locale || 'pt';
    const languageMap = {
      'pt': Language.PT_BR,
      'en': Language.EN,
      'es': Language.ES,
      'ar': Language.AR
    };

    try {
      if (onResendToken) {
        await onResendToken(values.email);
      } else {
        await AuthAPI.sendResetPasswordToken({
          email: values.email,
          isRetry,
          localServer: TEMPLATE,
          language: languageMap[currentLocale as keyof typeof languageMap]
        });
      }

      setIsBlocked(true);
      setLastSentAt(new Date());
      onClose();
      toast(t("login.forgot-password-token-sent"), { type: "success" });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return toast(t("login.user-not-found"), { type: "error" });
      }

      toast(t("login.token-error"), { type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <motion.form
      id="reset-password-form"
      onSubmit={handleSubmit}
      className="relative flex flex-col items-center gap-5 rounded-lg bg-app-gray-850 p-8"
      initial={{ opacity: 0, y: -50 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: { duration: 0.35 },
      }}
      exit={{ opacity: 0, y: 25, transition: { duration: 0.2 } }}
      transition={{ ease: "easeInOut" }}
    >
      <ModalCloseButton />

      <Dialog.Title className="text-lg font-semibold xs:text-xl sm:text-2xl md:text-3xl">
        {isRetry ? "Enviar um novo token" : t("recovery-pass")}
      </Dialog.Title>

      <InputControl
        autoFocus
        type="email"
        placeholder={t("inform-mail")}
        wrapperClassName="w-full max-w-[280px]"
        errorMessage={errors.email?.message}
        className="font-medium placeholder:font-bold"
        {...register("email")}
      />

      <div className="flex items-center gap-8">
        <Button
          type="button"
          onClick={onClose}
          variant="gray-darkless"
          disabled={isSubmitting}
        >
          {t("cancel")}
        </Button>

        <Button type="submit" disabled={isBlocked} isLoading={isSubmitting}>
          {isRetry ? t("Send") : t("Recovery")}
        </Button>
      </div>

      {isBlocked && <ResendTokenTimer lastSentAt={lastSentAt} />}
    </motion.form>
  );
};
